/**
 * AI Configuration Panel Component
 * 
 * Allows users to configure AI providers and API keys on the welcome screen.
 * Stores configurations securely in the database with encrypted API keys.
 * 
 * Flow: Select Provider → Enter API Key → Test & Fetch Models → Select Model → Save
 */

import { useState } from 'react';
import {
  Button,
  Modal,
  TextInput,
  Select,
  SelectItem,
  Tile,
  Toggle,
  TextArea,
  InlineNotification,
  SkeletonText,
  Tag,
  Loading,
} from '@carbon/react';
import {
  Add,
  Edit,
  TrashCan,
  Checkmark,
  WarningAlt,
  Settings,
} from '@carbon/icons-react';
import {
  useAIConfigs,
  useCreateAIConfig,
  useUpdateAIConfig,
  useDeleteAIConfig,
  useDefaultAIConfig,
  useLlmOverrideStatus,
} from '../../lib/query/hooks/useAIConfigs';
import ModelRolesPanel from './ModelRolesPanel';
import {
  AIProvider,
  PROVIDER_NAMES,
  PROVIDER_DESCRIPTIONS,
  DEFAULT_MODELS,
  CreateAIConfigRequest,
  aiConfigApi,
} from '../../lib/api/ai-config.api';
import { ApiError } from '../../lib/api/client';
import './AIConfigurationPanel.scss';

interface AIConfigFormData {
  provider: AIProvider;
  modelId: string;
  apiKey: string;
  displayName: string;
  description: string;
  baseUrl: string;
  isDefault: boolean;
}

const INITIAL_FORM_DATA: AIConfigFormData = {
  provider: 'openai',
  modelId: '',
  apiKey: '',
  displayName: '',
  description: '',
  baseUrl: '',
  isDefault: false,
};

type ConfigStep = 'provider' | 'apiKey' | 'model' | 'details';

export default function AIConfigurationPanel() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<AIConfigFormData>(INITIAL_FORM_DATA);
  const [showApiKey, setShowApiKey] = useState(false);
  const [currentStep, setCurrentStep] = useState<ConfigStep>('provider');
  
  // Model fetching state
  const [isTesting, setIsTesting] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [isApiKeyValid, setIsApiKeyValid] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Platform-global override status
  const { data: overrideStatus } = useLlmOverrideStatus();

  // Queries
  const { data: configs, isLoading, error } = useAIConfigs();
  const { data: defaultConfig } = useDefaultAIConfig();

  // Mutations
  const createMutation = useCreateAIConfig();
  const updateMutation = useUpdateAIConfig();
  const deleteMutation = useDeleteAIConfig();

  const handleOpenModal = (configId?: string) => {
    if (configId) {
      const config = configs?.find((c) => c.id === configId);
      if (config) {
        setEditingId(configId);
        setFormData({
          provider: config.provider,
          modelId: config.modelId,
          apiKey: '', // Don't pre-fill API key for security
          displayName: config.displayName || '',
          description: config.description || '',
          baseUrl: config.baseUrl || '',
          isDefault: config.isDefault,
        });
        // When editing, skip to details step
        setCurrentStep('details');
      }
    } else {
      setEditingId(null);
      setFormData(INITIAL_FORM_DATA);
      setCurrentStep('provider');
    }
    setIsModalOpen(true);
    setShowApiKey(false);
    setTestError(null);
    setSubmitError(null);
    setAvailableModels([]);
    setIsApiKeyValid(false);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setFormData(INITIAL_FORM_DATA);
    setShowApiKey(false);
    setCurrentStep('provider');
    setTestError(null);
    setSubmitError(null);
    setAvailableModels([]);
    setIsApiKeyValid(false);
  };

  const handleTestAndFetchModels = async () => {
    if (!formData.apiKey) {
      setTestError('Please enter an API key');
      return;
    }

    setIsTesting(true);
    setTestError(null);

    try {
      const result = await aiConfigApi.testAndFetchModels(
        formData.provider,
        formData.apiKey,
        formData.baseUrl || undefined
      );
      
      if (result.isValid) {
        setIsApiKeyValid(true);
        setAvailableModels(result.models);
        setTestError(null);
        // Auto-select first model if none selected
        if (!formData.modelId && result.models.length > 0) {
          setFormData({ ...formData, modelId: result.models[0] });
        }
        // Move to model selection step
        setCurrentStep('model');
      } else {
        setIsApiKeyValid(false);
        setTestError(result.error || 'Invalid API key');
      }
    } catch (error) {
      setIsApiKeyValid(false);
      setTestError(error instanceof Error ? error.message : 'Failed to test API key');
    } finally {
      setIsTesting(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    try {
      if (editingId) {
        // Update existing config
        await updateMutation.mutateAsync({
          id: editingId,
          data: {
            modelId: formData.modelId,
            ...(formData.apiKey && { apiKey: formData.apiKey }), // Only update if provided
            displayName: formData.displayName || undefined,
            description: formData.description || undefined,
            baseUrl: formData.baseUrl || undefined,
            isDefault: formData.isDefault,
          },
        });
      } else {
        // Create new config
        const createData: CreateAIConfigRequest = {
          provider: formData.provider,
          modelId: formData.modelId || DEFAULT_MODELS[formData.provider],
          apiKey: formData.apiKey,
          displayName: formData.displayName || undefined,
          description: formData.description || undefined,
          baseUrl: formData.baseUrl || undefined,
          isDefault: formData.isDefault,
        };
        await createMutation.mutateAsync(createData);
      }
      handleCloseModal();
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        setSubmitError(
          `A configuration with this provider and API key already exists. Please use a different API key or edit the existing configuration.`
        );
      } else {
        setSubmitError('Failed to save configuration. Please check your inputs and try again.');
      }
      console.error('Failed to save AI configuration:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this AI configuration?')) {
      try {
        await deleteMutation.mutateAsync(id);
      } catch (error) {
        console.error('Failed to delete AI configuration:', error);
      }
    }
  };

  const handleProviderChange = (provider: AIProvider) => {
    setFormData({
      ...formData,
      provider,
      modelId: '',
      apiKey: '',
    });
    setAvailableModels([]);
    setIsApiKeyValid(false);
    setTestError(null);
  };

  const handleNextStep = () => {
    if (currentStep === 'provider') {
      setCurrentStep('apiKey');
    } else if (currentStep === 'apiKey' && isApiKeyValid) {
      setCurrentStep('model');
    } else if (currentStep === 'model' && formData.modelId) {
      setCurrentStep('details');
    }
  };

  const handlePreviousStep = () => {
    if (currentStep === 'details') {
      setCurrentStep('model');
    } else if (currentStep === 'model') {
      setCurrentStep('apiKey');
    } else if (currentStep === 'apiKey') {
      setCurrentStep('provider');
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 'provider':
        return true;
      case 'apiKey':
        return isApiKeyValid && availableModels.length > 0;
      case 'model':
        return !!formData.modelId;
      case 'details':
        return true;
      default:
        return false;
    }
  };

  const getModalHeading = () => {
    if (editingId) return 'Edit AI Configuration';
    switch (currentStep) {
      case 'provider':
        return 'Step 1: Select AI Provider';
      case 'apiKey':
        return 'Step 2: Enter API Key';
      case 'model':
        return 'Step 3: Select Model';
      case 'details':
        return 'Step 4: Configuration Details';
      default:
        return 'Add AI Configuration';
    }
  };

  if (isLoading) {
    return (
      <Tile className="ai-config-panel">
        <div className="ai-config-panel__header">
          <Settings size={24} />
          <h3>AI Configuration</h3>
        </div>
        <SkeletonText paragraph lineCount={3} />
      </Tile>
    );
  }

  if (error) {
    return (
      <Tile className="ai-config-panel">
        <InlineNotification
          kind="error"
          title="Failed to load AI configurations"
          subtitle="Please try again later"
          lowContrast
        />
      </Tile>
    );
  }

  // ── Platform-global override banner ──────────────────────────────────────
  // When the server-wide LLM override is active, per-user config is disabled.
  if (overrideStatus?.overrideActive) {
    const { displayName, provider, baseUrl, models } = overrideStatus;
    const modelRows = [
      { role: 'Large',  model: models.large  || `${models.medium} (fallback)` },
      { role: 'Medium', model: models.medium || '—' },
      { role: 'Small',  model: models.small  || `${models.medium} (fallback)` },
      { role: 'Vision', model: models.vlm    || `${models.medium} (fallback)` },
    ];
    return (
      <Tile className="ai-config-panel ai-config-panel--override">
        <div className="ai-config-panel__header">
          <div className="ai-config-panel__header-left">
            <Settings size={24} />
            <h3>AI Configuration</h3>
          </div>
          <Tag type="blue" size="md">Platform Global Setting</Tag>
        </div>

        <InlineNotification
          kind="info"
          title="Managed by your platform administrator"
          subtitle="The LLM configuration is set at deployment level. Per-user configuration is disabled."
          lowContrast
          hideCloseButton
          style={{ marginBottom: '1rem' }}
        />

        <div className="ai-config-panel__override-details">
          {displayName && (
            <div className="ai-config-panel__override-row">
              <span className="ai-config-panel__override-label">Name</span>
              <strong>{displayName}</strong>
            </div>
          )}
          <div className="ai-config-panel__override-row">
            <span className="ai-config-panel__override-label">Provider</span>
            <Tag type="gray" size="sm">{provider}</Tag>
          </div>
          {baseUrl && (
            <div className="ai-config-panel__override-row">
              <span className="ai-config-panel__override-label">Endpoint</span>
              <code className="ai-config-panel__override-url">{baseUrl}</code>
            </div>
          )}
          <div className="ai-config-panel__override-models">
            <span className="ai-config-panel__override-label">Models by role</span>
            <table className="ai-config-panel__override-table">
              <tbody>
                {modelRows.map(({ role, model }) => (
                  <tr key={role}>
                    <td className="ai-config-panel__override-role">{role}</td>
                    <td><code>{model}</code></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Tile>
    );
  }

  const hasConfigs = configs && configs.length > 0;

  return (
    <>
      <Tile className="ai-config-panel">
        <div className="ai-config-panel__header">
          <div className="ai-config-panel__header-left">
            <Settings size={24} />
            <h3>AI Configuration</h3>
          </div>
          <Button
            kind="primary"
            size="sm"
            renderIcon={Add}
            onClick={() => handleOpenModal()}
          >
            Add Provider
          </Button>
        </div>

        {!hasConfigs ? (
          <div className="ai-config-panel__empty">
            <WarningAlt size={32} />
            <p>No AI providers configured</p>
            <p className="ai-config-panel__empty-subtitle">
              Add an AI provider to enable AI-powered features
            </p>
          </div>
        ) : (
          <div className="ai-config-panel__list">
            {configs.map((config) => (
              <div key={config.id} className="ai-config-card">
                <div className="ai-config-card__header">
                  <div className="ai-config-card__title">
                    <strong>{config.displayName || PROVIDER_NAMES[config.provider]}</strong>
                    {config.isDefault && (
                      <Tag type="blue" size="sm">
                        Default
                      </Tag>
                    )}
                    {!config.isActive && (
                      <Tag type="gray" size="sm">
                        Inactive
                      </Tag>
                    )}
                  </div>
                  <div className="ai-config-card__actions">
                    <Button
                      kind="ghost"
                      size="sm"
                      renderIcon={Edit}
                      iconDescription="Edit"
                      hasIconOnly
                      onClick={() => handleOpenModal(config.id)}
                    />
                    <Button
                      kind="ghost"
                      size="sm"
                      renderIcon={TrashCan}
                      iconDescription="Delete"
                      hasIconOnly
                      onClick={() => handleDelete(config.id)}
                    />
                  </div>
                </div>
                <div className="ai-config-card__details">
                  <p className="ai-config-card__provider">
                    {PROVIDER_NAMES[config.provider]}
                  </p>
                  <p className="ai-config-card__model">{config.modelId}</p>
                  {config.description && (
                    <p className="ai-config-card__description">{config.description}</p>
                  )}
                  {config.lastUsedAt && (
                    <p className="ai-config-card__usage">
                      Last used: {new Date(config.lastUsedAt).toLocaleDateString()} • 
                      Used {config.usageCount} times
                    </p>
                  )}
                </div>
                {config.isDefault && <ModelRolesPanel config={config} />}
              </div>
            ))}
          </div>
        )}

        {defaultConfig && (
          <div className="ai-config-panel__default-info">
            <Checkmark size={16} />
            <span>
              Default: {defaultConfig.displayName || PROVIDER_NAMES[defaultConfig.provider]} ({defaultConfig.modelId})
            </span>
          </div>
        )}
      </Tile>

      {/* Configuration Modal */}
      <Modal
        open={isModalOpen}
        onRequestClose={handleCloseModal}
        onRequestSubmit={currentStep === 'details' ? handleSubmit : handleNextStep}
        modalHeading={getModalHeading()}
        primaryButtonText={
          currentStep === 'details' 
            ? (editingId ? 'Update' : 'Add')
            : 'Next'
        }
        secondaryButtonText={currentStep === 'provider' ? 'Cancel' : 'Back'}
        onSecondarySubmit={currentStep === 'provider' ? handleCloseModal : handlePreviousStep}
        primaryButtonDisabled={
          !canProceed() ||
          (currentStep === 'details' && (!formData.apiKey && !editingId)) ||
          createMutation.isPending ||
          updateMutation.isPending
        }
        size="md"
      >
        <div className="ai-config-modal">
          {/* Step 1: Provider Selection */}
          {currentStep === 'provider' && (
            <>
              <Select
                id="provider"
                labelText="AI Provider"
                value={formData.provider}
                onChange={(e) => handleProviderChange(e.target.value as AIProvider)}
                disabled={!!editingId}
              >
                {Object.entries(PROVIDER_NAMES).map(([value, label]) => (
                  <SelectItem key={value} value={value} text={label} />
                ))}
              </Select>

              <p className="ai-config-modal__provider-description">
                {PROVIDER_DESCRIPTIONS[formData.provider]}
              </p>
            </>
          )}

          {/* Step 2: API Key Entry and Testing */}
          {currentStep === 'apiKey' && (
            <>
              <div className="ai-config-modal__step-info">
                <p><strong>Provider:</strong> {PROVIDER_NAMES[formData.provider]}</p>
              </div>

              {/* Base URL shown here (before the test) for providers that may use a
                  custom endpoint. For other providers it stays in the Details step. */}
              {(formData.provider === 'ica-nextgen' || formData.provider === 'ica-global' || formData.provider === 'openai') && (
                <TextInput
                  id="baseUrl-step2"
                  labelText="Base URL (Optional)"
                  helperText={
                    formData.provider === 'ica-nextgen'
                      ? 'Leave blank to use the default ICA Next-Gen endpoint. Enter your custom URL if your instance is hosted elsewhere.'
                      : formData.provider === 'ica-global'
                      ? 'Leave blank to use the default IBM Services Essentials endpoint. Enter your custom URL if required.'
                      : 'Leave blank to use the OpenAI default. Enter an OpenAI-compatible base URL to connect to a different endpoint.'
                  }
                  placeholder={
                    formData.provider === 'ica-nextgen'
                      ? 'https://api.nextgen-beta.ica.ibm.com/ica/v1'
                      : formData.provider === 'ica-global'
                      ? 'https://api.servicesessentials.ibm.com/v1'
                      : 'https://api.openai.com/v1'
                  }
                  value={formData.baseUrl}
                  onChange={(e) => {
                    setFormData({ ...formData, baseUrl: e.target.value });
                    setIsApiKeyValid(false);
                    setTestError(null);
                  }}
                />
              )}

              <TextInput
                id="apiKey"
                labelText="API Key"
                type={showApiKey ? 'text' : 'password'}
                placeholder="Enter your API key"
                value={formData.apiKey}
                onChange={(e) => {
                  setFormData({ ...formData, apiKey: e.target.value });
                  setIsApiKeyValid(false);
                  setTestError(null);
                }}
                helperText="Your API key will be encrypted and stored securely"
              />

              <Toggle
                id="showApiKey"
                labelText="Show API key"
                size="sm"
                toggled={showApiKey}
                onToggle={(checked) => setShowApiKey(checked)}
              />

              <Button
                kind="secondary"
                size="md"
                onClick={handleTestAndFetchModels}
                disabled={!formData.apiKey || isTesting}
                style={{ marginTop: '1rem' }}
              >
                {isTesting ? (
                  <>
                    <Loading small withOverlay={false} /> Testing...
                  </>
                ) : (
                  'Test API Key & Fetch Models'
                )}
              </Button>

              {testError && (
                <InlineNotification
                  kind="error"
                  title="API Key Test Failed"
                  subtitle={testError}
                  lowContrast
                  style={{ marginTop: '1rem' }}
                />
              )}

              {isApiKeyValid && availableModels.length > 0 && (
                <InlineNotification
                  kind="success"
                  title="API Key Valid"
                  subtitle={`Found ${availableModels.length} available models`}
                  lowContrast
                  style={{ marginTop: '1rem' }}
                />
              )}
            </>
          )}

          {/* Step 3: Model Selection */}
          {currentStep === 'model' && (
            <>
              <div className="ai-config-modal__step-info">
                <p><strong>Provider:</strong> {PROVIDER_NAMES[formData.provider]}</p>
                <p><strong>API Key:</strong> ••••••••</p>
              </div>

              <Select
                id="modelId"
                labelText="Select Model"
                value={formData.modelId}
                onChange={(e) => setFormData({ ...formData, modelId: e.target.value })}
              >
                <SelectItem value="" text="Choose a model" />
                {availableModels.map((model) => (
                  <SelectItem key={model} value={model} text={model} />
                ))}
              </Select>

              {availableModels.length === 0 && (
                <InlineNotification
                  kind="warning"
                  title="No models available"
                  subtitle="Please go back and test your API key again"
                  lowContrast
                  style={{ marginTop: '1rem' }}
                />
              )}
            </>
          )}

          {/* Step 4: Configuration Details */}
          {currentStep === 'details' && (
            <>
              <div className="ai-config-modal__step-info">
                <p><strong>Provider:</strong> {PROVIDER_NAMES[formData.provider]}</p>
                <p><strong>Model:</strong> {formData.modelId}</p>
              </div>

              <TextInput
                id="displayName"
                labelText="Display Name (Optional)"
                placeholder={`My ${PROVIDER_NAMES[formData.provider]} Config`}
                value={formData.displayName}
                onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
              />

              <TextArea
                id="description"
                labelText="Description (Optional)"
                placeholder="Add notes about this configuration..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />

              {/* Base URL for providers where it was not already entered in step 2 */}
              {formData.provider !== 'ica-nextgen' && formData.provider !== 'ica-global' && formData.provider !== 'openai' && (
                <TextInput
                  id="baseUrl"
                  labelText="Base URL (Optional)"
                  helperText="Override the default endpoint — for a Databricks model-serving endpoint or any other OpenAI-compatible gateway."
                  placeholder="https://adb-xxx.azuredatabricks.net/serving-endpoints/<endpoint>/invocations"
                  value={formData.baseUrl}
                  onChange={(e) => setFormData({ ...formData, baseUrl: e.target.value })}
                />
              )}
              {(formData.provider === 'ica-nextgen' || formData.provider === 'ica-global' || formData.provider === 'openai') && formData.baseUrl && (
                <p className="ai-config-modal__step-info" style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)', margin: '0 0 1rem' }}>
                  <strong>Base URL:</strong> {formData.baseUrl}
                </p>
              )}

              <Toggle
                id="isDefault"
                labelText="Set as default configuration"
                size="sm"
                toggled={formData.isDefault}
                onToggle={(checked) => setFormData({ ...formData, isDefault: checked })}
              />
            </>
          )}

          {submitError && (
            <InlineNotification
              kind="error"
              title="Configuration error"
              subtitle={submitError}
              lowContrast
              style={{ marginTop: '1rem' }}
            />
          )}
        </div>
      </Modal>
    </>
  );
}

// Made with Bob
